import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Modal, Spin } from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { all_routes } from "../../../router/all_routes";
import Table from "../../Common/dataTable/index";
import ActionsDropdown, { Action } from "../../Common/ActionsDropdown";
import PageHeader from "components/Common/PageHeader";
import CustomButton from "components/Common/ui/CustomButton";
import TableFilterDynamique from "components/Common/TableFilter/TableFilterDynamique";
import { useAbility } from "config/abilityContext";
import useTeacherManagement from "./hooks/useTeacherManagement";
import { FilterField } from "components/Common/TableFilter/TableFilter";
import TeacherImportModal from "feature-module/import/components/TeacherImportModal";

const { confirm } = Modal;

const TeacherList = () => {
  const { t } = useTranslation();
  const routes = all_routes;
  const ability = useAbility();

  const {
    teachers,
    isLoading,
    fetchTeachers,
    pagination,
    handleDelete
  } = useTeacherManagement();

  const [filters, setFilters] = useState<{ [key: string]: string | number | null }>({});

  const filterFields: FilterField[] = [
    { key: "firstName", label: t("first_name"), type: "text" },
    { key: "lastName", label: t("last_name"), type: "text" },
    { key: "email", label: t("email"), type: "text" },
  ];

  const handleFilterApply = (newFilters: { [key: string]: string | number | null }) => {
    setFilters(newFilters);
    fetchTeachers({
      ...newFilters,
      page: 1,
      limit: pagination?.limit || 10,
    });
  };

  const handleFilterReset = () => {
    setFilters({});
    fetchTeachers({
      page: 1,
      limit: pagination?.limit || 10,
    });
  };

  const handleTableChange = (pg: any) => {
    fetchTeachers({
      ...filters,
      page: pg?.current || 1,
      limit: pg?.pageSize || 10,
    });
  };

  const handleDeleteTeacher = async (teacherId: string) => {
    const res = await handleDelete(teacherId)
    if (res) {
      fetchTeachers({ ...filters, page: pagination.page, limit: pagination.limit });
    }
  };

  const showDeleteConfirm = (teacherId: string) => {
    confirm({
      title: t("are_you_sure_you_want_to_delete_this_teacher?"),
      icon: <ExclamationCircleOutlined />,
      content: t("this_action_cannot_be_undone"),
      okText: t("delete"),
      okType: "danger",
      cancelText: t("cancel"),
      onOk: () => handleDeleteTeacher(teacherId),
    });
  };

  const columns = [
    {
      title: t("first_name"),
      dataIndex: ["user", "firstName"],
      sorter: false,
    },
    {
      title: t("last_name"),
      dataIndex: ["user", "lastName"],
      sorter: false,
    },
    {
      title: t("email"),
      dataIndex: ["user", "email"],
      render: (_: any, record: any) => (
        <Link to={ability.can("getAll", "Teacher") ?
          routes.teacherDetails.replace(":id", record.id) + routes.teacherDetailsTabs.details : "#"}
          className="link-primary"
        >
          {record.user.email}
        </Link>
      ),
      sorter: false,
    },
    {
      title: t("phone_number"),
      dataIndex: ["user", "phoneNumber"],
      render: (phoneNumber: string) => phoneNumber || "-",
      sorter: false,
    },
    {
      title: t("action"),
      dataIndex: "action",
      render: (_: any, record: any) => {
        const actions: Action[] = [
          {
            label: t("details"),
            icon: "ti ti-eye",
            actionType: "link",
            hidden: !ability.can("getAll", "Teacher"),
            to: `${routes.teacherDetails.replace(':id', record.id)}${routes.teacherDetailsTabs.details}`
          },
          {
            label: t("view_routine"),
            icon: "ti ti-menu",
            actionType: "link",
            hidden: !ability.can("getAll", "Planner"),
            to: `${routes.teacherDetails.replace(':id', record.id)}${routes.teacherDetailsTabs.routine}`
          },
          {
            label: t("edit"),
            icon: "ti ti-edit-circle",
            actionType: "link",
            hidden: !ability.can("update", "Teacher"),
            to: routes.editTeacher.replace(':id', record.id),
          },
          {
            label: t("delete"),
            icon: "ti ti-trash-x",
            actionType: "modal",
            hidden: !ability.can("delete", "Teacher"),
            onClick: () => showDeleteConfirm(record.id),
          },
        ];
        return <ActionsDropdown actions={actions} />;
      }
    }
  ];

  const breadcrumbItems = [
    { label: t("dashboard"), path: all_routes.dashboard },
    { label: t("teachers"), active: true },
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <PageHeader
          title={t("teachers_list")}
          breadcrumbItems={breadcrumbItems}
        />

        <div className="d-flex mb-3 justify-content-end gap-2">
          {ability.can("create", "Teacher") && (<>
            <TeacherImportModal onImportSuccess={fetchTeachers} />
            <CustomButton
              to={routes.addTeacher}
              variant="primary"
              size="md"
              disabled={false}
              onClick={() => { }}
              asLink
            >
              <i className="ti ti-square-rounded-plus me-2" />
              {t("add_teacher")}
            </CustomButton>
          </>
          )}
          <TableFilterDynamique
            fields={filterFields}
            filters={filters}
            onApply={handleFilterApply}
            onReset={handleFilterReset}
          />
        </div>

        <div className="table-responsive">
          {isLoading ? (
            <Spin size="large" className="d-flex justify-content-center my-5" />
          ) : (
            <Table
              dataSource={teachers}
              columns={columns}
              pagination={true}

              dataPaginate={{
                current: pagination.page,
                pageSize: pagination.limit,
                total: pagination.total,

              }}
              onChange={handleTableChange}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherList;